import classNames from 'classnames'
import { lazy, Suspense, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BackButton, Loader, MainButton, useIsMobile } from '@ds'
import { Inventory, webApp } from '@shared'
import {
  PrologueStage,
  useAvailableQuestsState,
  usePrologue,
  usePrologueActions,
  usePrologueStepState,
} from '@store'

import styles from './Prologue.module.scss'
import { TooltipTaps, TooltipUserLevel } from './components'
import { preloadSounds, PrologueSounds, unloadAllSounds } from './soundManager'
import { PrologQuests } from './types'

const prologueStepComponents: Record<
  string,
  React.LazyExoticComponent<React.ComponentType<any>>
> = {
  step1: lazy(() =>
    import('./steps/Step1').then((mod) => ({ default: mod.Step1 }))
  ),
  step2: lazy(() =>
    import('./steps/StepAuthorPhrase').then((mod) => ({
      default: mod.StepAuthorPhrase,
    }))
  ),
  step3: lazy(() =>
    import('./steps/Step3').then((mod) => ({ default: mod.Step3 }))
  ),
  step4: lazy(() =>
    import('./steps/StepAuthorPhrase').then((mod) => ({
      default: mod.StepAuthorPhrase,
    }))
  ),
  step5: lazy(() =>
    import('./steps/Step5').then((mod) => ({ default: mod.Step5 }))
  ),
  step6: lazy(() =>
    import('./steps/StepEchoPhrase').then((mod) => ({
      default: mod.StepEchoPhrase,
    }))
  ),
  step7: lazy(() =>
    import('./steps/Step7').then((mod) => ({ default: mod.Step7 }))
  ),
  step8: lazy(() =>
    import('./steps/StepAuthorPhrase').then((mod) => ({
      default: mod.StepAuthorPhrase,
    }))
  ),
  step9: lazy(() =>
    import('./steps/Step9').then((mod) => ({ default: mod.Step9 }))
  ),
  step10: lazy(() =>
    import('./steps/StepAuthorPhrase').then((mod) => ({
      default: mod.StepAuthorPhrase,
    }))
  ),
  step11: lazy(() =>
    import('./steps/Step11').then((mod) => ({ default: mod.Step11 }))
  ),
  step12: lazy(() =>
    import('./steps/StepAuthorPhrase').then((mod) => ({
      default: mod.StepAuthorPhrase,
    }))
  ),
}

const stepByQuest: Record<PrologQuests, number> = {
  split: 1,
  not: 6,
  rhomb: 8,
  spiral: 10,
}

export const Prologue = () => {
  const navigate = useNavigate()
  const userProgressVisible = usePrologue.store.isUserProgressVisible()
  const tapsVisible = usePrologue.store.isTapsVisible()
  const prologueStage = usePrologue.store.prologueStage()
  const showInventory = usePrologue.store.isInventoryVisible()
  const step = usePrologueStepState()
  const availableQuests = useAvailableQuestsState()
  const { setStep, nextStep, setPrologueStage } = usePrologueActions()
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  useEffect(() => {
    if (prologueStage === PrologueStage.Eye) {
      navigate('/home')
    }
  }, [prologueStage])

  useEffect(() => {
    preloadSounds()

    return () => unloadAllSounds()
  }, [])

  useEffect(() => {
    if (step === null) {
      const firstUncompletedIndex = availableQuests.find(
      // @ts-ignore TODO: FIx after release
        (q) => q.type === 'prologue' && ((!q.isSolved && q.isAvailable) || (q.isSolved && q.starsCollected !== q.starsTotal) )
      )
      const eyeQuest = availableQuests.find((item) => item.slug === 'eye')

      if (!firstUncompletedIndex) {
        if (eyeQuest?.isSolved && !eyeQuest.isAvailable && (eyeQuest.starsTotal === eyeQuest.starsCollected)) {
          setPrologueStage(PrologueStage.Finished)
          navigate('/home')
          return
        }

        setPrologueStage(PrologueStage.Eye)
        return
      }

      setStep(stepByQuest[firstUncompletedIndex.slug as PrologQuests])
    }
  }, [step, availableQuests])

  const stepPropsMap: Record<string, any> = useMemo(
    () => ({
      step2: {
        phrase: t('prologue.step2.text'),
        nextStep: nextStep,
        soundPhrase: PrologueSounds.Aeron1,
      },
      step4: {
        phrase: t('prologue.step4.text'),
        nextStep: nextStep,
        soundPhrase: PrologueSounds.Aeron2,
      },
      step6: {
        phrase: t('prologue.step6.text'),
        nextStep: nextStep,
        soundPhrase: PrologueSounds.Echo2,
      },
      step8: {
        phrase: t('prologue.step8.text'),
        nextStep: nextStep,
        soundPhrase: PrologueSounds.Aeron3,
      },
      step10: {
        phrase: t('prologue.step10.text'),
        nextStep: nextStep,
        soundPhrase: PrologueSounds.Aeron4,
      },
      step12: {
        phrase: t('prologue.step12.text'),
        nextStep: () => {
          setPrologueStage(PrologueStage.Eye)
        },
        soundPhrase: PrologueSounds.Aeron5,
      },
    }),
    []
  )

  if (step === null) {
    return <Loader />
  }

  const stepProps = stepPropsMap[`step${step}`] || {}

  const PrologueComponent =
    prologueStepComponents[`step${step}` as keyof typeof prologueStepComponents]

  return (
    <div
      className={classNames([
        styles.prologue,
        isMobile && webApp?.isFullscreen && styles.isFullscreen,
      ])}
    >
      <BackButton hidden />
      <MainButton hidden />
      {showInventory && <Inventory />}

      {userProgressVisible && (
        <div className={styles.userLevel}>
          <TooltipUserLevel />
        </div>
      )}
      <Suspense fallback={<Loader />}>
        <PrologueComponent {...stepProps} />
      </Suspense>

      {tapsVisible && (
        <div className={styles.taps}>
          <div className={styles.enter}>
            <TooltipTaps />
          </div>
        </div>
      )}
    </div>
  )
}
